import { arrayOf, number, shape } from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { formatDateLocal } from 'Helpers/date-utils';
import DOMPurify from 'dompurify';

import McNotes from './McNotes';
import FullContentNotesModal from './FullContentNotesModal';
import styles from './styles.less';

const MC_COUNT_THRESHOLD = 3;

// check if displayed height is less than the actual height
function isContentClipped(divElement) {
  try {
    return divElement.scrollHeight > divElement.clientHeight;
  } catch (e) {
    return false;
  }
}

const NotesForProvider = ({ mcCount, lastConsultReasons, conversationActiveId }) => {
  const [isExpanded, setExpanded] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [isClipped, setIsClipped] = useState(false);
  const previewRef = useRef(null);

  const displayMcCount = !!mcCount && mcCount >= MC_COUNT_THRESHOLD;
  const displayLastConsultReasons = !!lastConsultReasons.length;

  // replace all the new line characters with <br/> to display the text correctly
  let reasonTextDisplay = '';
  lastConsultReasons.forEach((item, i) => {
    reasonTextDisplay += `<b>[${formatDateLocal(item.createdAt)}]</b> ${item.reason} ${
      i === 0 ? '\n' : ''
    }`;
  });

  const content = DOMPurify.sanitize(reasonTextDisplay);

  // open full notes content when conversation active id is changed
  useEffect(() => {
    setExpanded(true);
  }, [conversationActiveId]);

  useEffect(() => {
    const previewElement = previewRef.current;

    const observer = new ResizeObserver(() => {
      setIsClipped(isContentClipped(previewElement));
    });

    if (previewElement) {
      observer.observe(previewElement);
    }

    return () => {
      if (previewElement) {
        observer.unobserve(previewElement);
      }
    };
  }, [isExpanded, lastConsultReasons]);

  if (!displayMcCount && !displayLastConsultReasons) {
    return null;
  }

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.titleWrapper} onClick={() => setExpanded(!isExpanded)}>
          <span>Notes for Provider</span>
          <Icon name={isExpanded ? 'chevron up' : 'chevron down'} />
        </div>

        {isExpanded && (
          <>
            {displayMcCount && <McNotes mcCount={mcCount} />}
            {displayLastConsultReasons && (
              <div className={styles.lastConsultReasonsWrapper}>
                <div className={styles.lastConsultReasonsTitle}>Last consultations’ reasons</div>
                <span
                  ref={previewRef}
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                />
                {isClipped && (
                  <div style={{ alignSelf: 'end' }}>
                    <Button className={styles.moreBtn} onClick={() => setOpenModal(true)}>
                      See more
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {openModal && (
        <FullContentNotesModal
          mcCount={mcCount}
          reasonTextDisplay={reasonTextDisplay}
          onClose={() => setOpenModal(false)}
        />
      )}
    </>
  );
};

NotesForProvider.propTypes = {
  mcCount: number.isRequired,
  lastConsultReasons: arrayOf(shape({})).isRequired,
  conversationActiveId: number.isRequired,
};

export default NotesForProvider;
